import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import caretDownIcon from "@phosphor-icons/core/regular/caret-down.svg";

export type MobileTabName = "instructions" | "editor" | "output";

type Props = {
    contentIds: Record<MobileTabName, string>;
    fileSelectMenuTriggerRef?: React.RefObject<HTMLButtonElement>;
    hasInstructions: boolean;
    isInitialTabSelection: boolean;
    onToggleFileSelectMenu?: () => void;
    selectedFilename: string;
    selectedTab: MobileTabName;
    setSelectedTab: (tab: MobileTabName) => void;
    useEmbeddedStyles?: boolean;
};

function TabButton({
    isSelected,
    contentId,
    onClick,
    label,
}: {
    isSelected: boolean;
    contentId: string;
    onClick: () => void;
    label: string;
}): React.ReactElement {
    const styles = standaloneStyles;
    return (
        <button
            role="tab"
            className={css(
                styles.tab,
                isSelected ? styles.selectedTab : styles.unselectedTab,
            )}
            onClick={onClick}
            aria-selected={isSelected}
            aria-controls={contentId}
        >
            <LabelLarge>{label}</LabelLarge>
        </button>
    );
}

export default function MobileTabList({
    contentIds,
    fileSelectMenuTriggerRef,
    hasInstructions,
    isInitialTabSelection,
    onToggleFileSelectMenu,
    selectedFilename,
    selectedTab,
    setSelectedTab,
    useEmbeddedStyles,
}: Props): React.ReactElement {
    const getTabProps = (tab: MobileTabName) => ({
        isSelected: selectedTab === tab,
        contentId: contentIds[tab],
        onClick: () => setSelectedTab(tab),
    });

    const styles = useEmbeddedStyles ? embeddedStyles : standaloneStyles;
    return (
        <View style={styles.mobileTabList} role="tablist">
            {hasInstructions && (
                <TabButton
                    {...getTabProps("instructions")}
                    label={doNotTranslateYet`Instructions`}
                />
            )}
            {selectedTab === "editor" && onToggleFileSelectMenu != null ? (
                <button
                    // We don't want to steal focus on page load, so we only set the
                    // autoFocus prop to true if the user is switching to the tab.
                    autoFocus={!isInitialTabSelection}
                    role="listbox"
                    className={css(
                        styles.tab,
                        styles.selectedTab,
                        styles.fileSelectOpener,
                    )}
                    aria-label={doNotTranslateYet`Select a file`}
                    onClick={(e) => {
                        const wasActivatedByKeyboard = e.detail === 0;
                        if (!wasActivatedByKeyboard) {
                            onToggleFileSelectMenu();
                        }
                    }}
                    onKeyUp={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            // handle key 'click' events on keyup, for consistency
                            // with the wonder blocks buttons. mixing and matching
                            // can lead to a single keypress being registered
                            // twice, if the first button registers the keydown
                            // and then focuses a button that registers the
                            // keyup.
                            onToggleFileSelectMenu();
                        }
                    }}
                    ref={fileSelectMenuTriggerRef}
                >
                    <LabelLarge>{selectedFilename}</LabelLarge>
                    <PhosphorIcon icon={caretDownIcon} />
                </button>
            ) : (
                <TabButton
                    {...getTabProps("editor")}
                    label={selectedFilename}
                />
            )}
            <TabButton
                {...getTabProps("output")}
                label={doNotTranslateYet`Output`}
            />
        </View>
    );
}

function getBorderAndPaddingStyles(borderWidth: number): CSSProperties {
    return {
        borderWidth,
        paddingTop: 2 - borderWidth,
        paddingBottom: 2 - borderWidth,
        paddingLeft: spacing.small_12 - borderWidth,
        paddingRight: spacing.small_12 - borderWidth,
    };
}
/* eslint-disable react-native/no-unused-styles */
const getStyles = (useEmbeddedStyles: boolean) =>
    StyleSheet.create({
        mobileTabList: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
            backgroundColor: color.offBlack16,
            display: "flex",
            flexDirection: "row",
            gap: spacing.xxSmall_6,
            paddingTop: spacing.xxSmall_6,
            paddingBottom: spacing.xxSmall_6,
            paddingLeft: "calc(47% - 75px)",
            paddingRight: "calc(47% - 75px)",
            overflowX: "auto",
            flexShrink: 0,
        },
        tab: {
            height: 24,
            flexBasis: 150,
            flexShrink: 0,
            borderRadius: 4,
            alignItems: "center",
            borderStyle: "solid",
            ":hover": getBorderAndPaddingStyles(2),
            ":focus": getBorderAndPaddingStyles(2),
            ":active": getBorderAndPaddingStyles(2),
        },
        selectedTab: {
            backgroundColor: color.white,
            color: color.offBlack,
            ...getBorderAndPaddingStyles(1),
            borderColor: color.offBlack32,
            ":active": {
                borderColor: color.offBlack50,
            },
        },
        unselectedTab: {
            backgroundColor: "#cbcbcb",
            color: color.offBlack,
            ...getBorderAndPaddingStyles(0),
            borderColor: color.offBlack16,
            ":active": {
                borderColor: color.offBlack32,
            },
        },
        fileSelectOpener: {
            display: "grid",
            gridTemplateColumns: "1fr auto",
        },
    });

const embeddedStyles = getStyles(true);
const standaloneStyles = getStyles(false);
